/**
 * Goal MILESTONES — the signal that makes a goal a *goal* and not an issue: a
 * longer arc broken into named checkpoints. Shared by the goals home (one
 * segmented track per row — an issue ends in a due date, a goal ends in its
 * milestone track) and the goal detail page (the full stepper), so the two read
 * as one system.
 *
 * Pure render + pure functions (no hooks, no server imports) so it's safe in
 * both RSC and client trees — same discipline as `goal-definition.tsx`.
 *
 * Graduated from the /styleguide/goals prototype.
 */

import * as React from "react";
import { cn } from "../../utils";
import { CheckCircle2, Circle, Flag } from "../../icons";

export type MilestoneStatus = "done" | "current" | "upcoming";

/** One checkpoint on the way to the goal. Maps 1:1 off an
 *  `excellent_goal_milestones` row. */
export interface GoalMilestone {
  uid?: string;
  label: string;
  /** The checkpoint's marker — a date, a value, a gate. */
  hint: string | null;
  status: MilestoneStatus;
}

const MILESTONE_STATUSES: MilestoneStatus[] = ["done", "current", "upcoming"];

/**
 * Parse the `milestones` JSON column off `excellent_goals` into a typed,
 * defensive array — same discipline as `parseCriteria` (its sibling on the same
 * row). Shape: `[{ label, hint?, status }]`. A bad/absent status falls back to
 * "upcoming"; malformed JSON yields `[]`.
 */
export function parseMilestones(json: string | null): GoalMilestone[] {
  if (!json) return [];
  try {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) return [];
    return arr
      .filter((x) => x && typeof x.label === "string")
      .map((x) => ({
        uid: typeof x.uid === "string" ? x.uid : undefined,
        label: String(x.label),
        hint: x.hint == null ? null : String(x.hint),
        status: (MILESTONE_STATUSES as string[]).includes(x.status)
          ? (x.status as MilestoneStatus)
          : "upcoming",
      }));
  } catch {
    return [];
  }
}

/** done / total across a milestone set — the row's "n/m" + track fill. */
export function milestoneProgress(ms: GoalMilestone[]): { done: number; total: number } {
  return { done: ms.filter((m) => m.status === "done").length, total: ms.length };
}

/** The checkpoint in flight (or the next one up) — the row's "what's happening". */
export function currentMilestone(ms: GoalMilestone[]): GoalMilestone | undefined {
  return ms.find((m) => m.status === "current") ?? ms.find((m) => m.status === "upcoming");
}

// ── Compact (list-row) track ─────────────────────────────────────────────────

/**
 * The milestone track: fixed-width, flex segments — done · current · upcoming.
 * `flex-1` segments mean the track is the same width no matter the count, so the
 * right edge is flush across every row (it lives in one shared trailing column).
 *
 * NOTE: the goals ledger no longer renders this. A list row shows ONE meter —
 * `<HealthBar>` — whatever is being measured, because a column of segmented
 * tracks reads as confetti (the same reason HealthBar itself replaced a
 * segmented meter). This stays for the detail-page stepper's neighbourhood,
 * where a single goal's named checkpoints are the subject rather than one row
 * among forty.
 */
export function MilestoneTrack({ milestones }: { milestones: GoalMilestone[] }) {
  return (
    <span className="flex w-full items-center gap-1" aria-hidden>
      {milestones.map((m, i) => (
        <span
          key={m.uid ?? i}
          title={m.hint ? `${m.label} · ${m.hint}` : m.label}
          className={cn(
            "h-1.5 flex-1 rounded-full",
            m.status === "done"
              ? "bg-success"
              : m.status === "current"
                ? "bg-primary"
                : "bg-border",
          )}
        />
      ))}
    </span>
  );
}

/** The trailing block for a goal row: `[Now/Next label (lg)] [n/m] [track]`.
 *  A goal with no milestones renders the one-line "shape it" prompt instead. */
export function MilestoneRowTrailing({
  milestones,
  /** Widths kept as props so the home can align every row's columns. */
  nextClassName = "hidden w-48 shrink-0 items-center gap-1 truncate type-subtext text-muted-foreground lg:flex",
  countClassName = "w-9 shrink-0 text-right type-subtext tabular-nums text-muted-foreground",
  trackClassName = "w-28 shrink-0",
}: {
  milestones: GoalMilestone[];
  nextClassName?: string;
  countClassName?: string;
  trackClassName?: string;
}) {
  const { done, total } = milestoneProgress(milestones);
  const cur = currentMilestone(milestones);
  if (total === 0) {
    return (
      <span className="ml-auto inline-flex shrink-0 items-center gap-1.5 type-subtext text-muted-foreground">
        <Flag className="size-3" />
        No milestones
      </span>
    );
  }
  return (
    <>
      <span className={nextClassName}>
        <span className="text-muted-foreground/60">
          {cur ? (cur.status === "current" ? "Now" : "Next") : "Done"}
        </span>
        <span className="truncate text-foreground/80">
          {cur ? cur.label : "All milestones met"}
        </span>
      </span>
      <span className={countClassName}>
        {done}/{total}
      </span>
      <span className={trackClassName}>
        <MilestoneTrack milestones={milestones} />
      </span>
    </>
  );
}

// ── Full (detail-page) stepper ───────────────────────────────────────────────

function MilestoneStep({ m, last }: { m: GoalMilestone; last: boolean }) {
  const marker =
    m.status === "done" ? (
      <CheckCircle2 className="size-4 text-success" />
    ) : m.status === "current" ? (
      <span className="flex size-4 items-center justify-center">
        <span className="size-2.5 rounded-full bg-primary ring-4 ring-primary/15" />
      </span>
    ) : (
      <Circle className="size-4 text-muted-foreground/40" />
    );
  return (
    <li className="flex gap-3">
      <div className="flex flex-col items-center">
        <span className="mt-0.5 shrink-0">{marker}</span>
        {!last ? <span className="mt-1 w-px flex-1 bg-border" /> : null}
      </div>
      <div className={cn("flex min-w-0 flex-1 items-baseline justify-between gap-3", !last && "pb-4")}>
        <span
          className={
            m.status === "current"
              ? "font-medium text-foreground"
              : m.status === "done"
                ? "text-muted-foreground"
                : "text-foreground"
          }
        >
          {m.label}
        </span>
        <span className="shrink-0 type-subtext tabular-nums text-muted-foreground">
          {m.hint ??
            (m.status === "done" ? "done" : m.status === "current" ? "in progress" : "upcoming")}
        </span>
      </div>
    </li>
  );
}

/** The full milestone stepper for the goal detail page. A goal with none gets a
 *  guiding empty (never a dead box), so "shape the arc" is one click away. */
export function MilestoneStepper({
  milestones,
  addHref,
}: {
  milestones: GoalMilestone[];
  /** Href for the inline "Add milestones" affordance on an unshaped goal. */
  addHref?: string;
}) {
  if (milestones.length === 0) {
    return (
      <div className="flex flex-col items-start gap-2 rounded-md border border-dashed border-border/70 px-3 py-3">
        <span className="type-body text-muted-foreground">
          No milestones yet — break the arc into a few named checkpoints so
          progress is legible and the home row can show where you are.
        </span>
        <a
          href={addHref ?? "#"}
          className="inline-flex items-center gap-1.5 type-body text-primary hover:underline"
        >
          <Flag className="size-3.5" />
          Add milestones
        </a>
      </div>
    );
  }
  return (
    <ol className="flex flex-col">
      {milestones.map((m, i) => (
        <MilestoneStep key={m.uid ?? m.label} m={m} last={i === milestones.length - 1} />
      ))}
    </ol>
  );
}

